/**
 * TeamList Component - Hedita Design System
 * Feature: Team - Lista collaboratori
 */

import { useState } from "react";
import { Plus, Search, Users, Mail, Phone, Shield, User, Trash2, Pencil } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useCollaboratori, useDeleteCollaboratore } from "../hooks";
import type { Collaboratore } from "../types";

const RUOLI_CONFIG: Record<string, { bg: string; color: string; border: string; label: string }> = {
  "admin": { bg: "bg-primary/10", color: "text-primary", border: "border-primary/20", label: "Admin" },
  "manager": { bg: "bg-sky-500/10", color: "text-sky-400", border: "border-sky-500/20", label: "Manager" },
  "operatore": { bg: "bg-muted/30", color: "text-muted-foreground", border: "border-border", label: "Operatore" },
  "pulizie": { bg: "bg-emerald-500/10", color: "text-emerald-400", border: "border-emerald-500/20", label: "Pulizie" },
  "manutenzione": { bg: "bg-amber-500/10", color: "text-amber-400", border: "border-amber-500/20", label: "Manutenzione" },
};

export function TeamList() {
  const [search, setSearch] = useState("");
  const { data: collaboratori = [], isLoading } = useCollaboratori();
  const deleteCollaboratore = useDeleteCollaboratore();

  const filtered = collaboratori.filter(c => 
    !search || 
    c.nome?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Team
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Gestisci collaboratori</p>
        </div>
        <button 
          onClick={() => window.location.href = "/team/collaboratori/nuovo"}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all shadow-sm"
        >
          <Plus className="h-4 w-4" /> Nuovo
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Cerca collaboratore..."
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:border-primary/50 transition-all"
        />
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-secondary/50 border border-border/50 flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <p className="font-medium text-foreground mb-1">Nessun collaboratore</p>
          <p className="text-sm text-muted-foreground mb-4">Aggiungi il primo membro del team</p>
          <button 
            onClick={() => window.location.href = "/team/collaboratori/nuovo"}
            className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all"
          >
            <Plus className="h-4 w-4 inline mr-1" /> Aggiungi
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((c: Collaboratore) => {
            const ruolo = RUOLI_CONFIG[c.ruolo || "operatore"] || RUOLI_CONFIG["operatore"];
            
            return (
              <div 
                key={c.id} 
                className="bg-card border border-border rounded-2xl p-5 hover:border-primary/30 transition-all group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/10 flex items-center justify-center text-lg font-semibold text-primary flex-shrink-0">
                      {c.nome?.charAt(0) || "?"}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-sm">{c.nome}</h3>
                      <span className={`inline-block px-2 py-0.5 rounded-lg text-[10px] font-medium border ${ruolo.bg} ${ruolo.color} ${ruolo.border}`}>
                        {ruolo.label}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => window.location.href = `/team/collaboratori/${c.id}`}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => deleteCollaboratore.mutate(c.id)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-border/50 space-y-2">
                  {c.email && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" />
                      <span>{c.email}</span>
                    </div>
                  )}
                  {c.telefono && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      <span>{c.telefono}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
